import { Badge } from "@/components/Badge";
import { DownloadButton } from "@/components/DownloadButton";
import { ExportStatus } from "@/lib/utils/exportStatus";
import { statusToBadgeColor } from "@/lib/utils/statusTobadgeColor";
import { parseDateWithHours } from "@/lib/utils";
import { ColumnDef } from "@tanstack/react-table";
import { Link } from "@heroui/react";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export type S2DataUpload = {
  id: string;
  generated_at: string;
  client: string;
  report_date: string;
  user: string;
  file_name: string;
  status: string;
};

export function useS2DataUploadColumns(): ColumnDef<S2DataUpload>[] {
  const { t } = useTranslation();

  return useMemo(
    () => [
      { accessorKey: "id", header: "Id" },
      {
        accessorKey: "file_name",
        header: t("data_upload.fields.file_name"),
        cell: ({ getValue }) => {
          const value = getValue() as string;
          return (
            <span className="whitespace-nowrap overflow-hidden text-ellipsis block">
              {value}
            </span>
          );
        },
      },
      { accessorKey: "user_name", header: t("data_upload.fields.user_name") },
      {
        accessorKey: "status",
        header: t("data_upload.fields.status"),
        cell: ({ getValue }) => {
          const value = getValue() as ExportStatus;
          const badgeColor = statusToBadgeColor[value] || "error";
          return <Badge color={badgeColor} />;
        },
      },
      {
        accessorKey: "provider_config_name",
        header: t("data_upload.fields.configuration"),
      },
      {
        accessorKey: "created_at",
        header: t("data_upload.fields.created_at"),
        cell: ({ row }) => {
          const cell_data: string | undefined = row.getValue("created_at");
          if (cell_data) {
            return parseDateWithHours(cell_data);
          }
          return "";
        },
      },
      {
        accessorKey: "updated_at",
        header: t("data_upload.fields.updated_at"),
        cell: ({ row }) => {
          const cell_data: string | undefined = row.getValue("updated_at");
          if (cell_data) {
            return parseDateWithHours(cell_data);
          }
          return "";
        },
      },
      {
        accessorKey: "download",
        header: t("data_upload.fields.download"),
        cell: ({ row }) => {
          const id = row.getValue("id") as string;
          const fileName = row.getValue("file_name") as string;
          const href = `${
            import.meta.env.VITE_BACKEND_URL
          }/s2/s2_data_upload/download/${id}`;

          return (
            <div className="flex flex-row items-center justify-between w-full">
              <DownloadButton
                href={href}
                filename={`${fileName}`}
                size="sm"
                variant="light"
                color="primary"
                isIconOnly
              />
              <div className="flex justify-end">
                <Link
                  href={`${window.location.pathname}/${row.getValue("id")}`}
                  className="text-xs"
                  underline="hover"
                >
                  {t("actions.details")}
                </Link>
              </div>
            </div>
          );
        },
      },
    ],
    [t],
  );
}
